import { useState } from 'react';
import normalizeUrl from 'normalize-url';
import { useQueryClient } from '@tanstack/react-query';
import { isUrlReachableQueryOptions } from '@/lib/query-options';

export const useUrlValidation = () => {
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const queryClient = useQueryClient();

  const validateUrl = async (url: string): Promise<string> => {
    let normalized = '';
    setIsFetching(true); // Set fetching to true when starting the fetch
    setError(null); // fresh attempt — clear any prior message
    try {
      normalized = normalizeUrl(url, {
        defaultProtocol: 'https',
        stripWWW: false,
      });
    } catch (err) {
      setError('That does not look like a valid URL.');
      setIsFetching(false); // Reset fetching state on error
      throw new Error('URL is not valid');
    }

    try {
      const result = await queryClient.fetchQuery(isUrlReachableQueryOptions(normalized));
      setIsFetching(false); // Reset fetching state after fetch completes
      if (!result) {
        setError('That URL could not be reached. Check it is correct and try again.');
        throw new Error('URL is not reachable');
      }
      setError(null);
      return normalized; // Return normalized URL on success
    } catch (err) {
      setIsFetching(false); // Reset fetching state on error
      // Keep the specific "not reachable" message set above; only fall back to a user-facing generic
      // for an unexpected failure of the reachability check itself (never the app-sounding original).
      setError((prev) => prev ?? 'Could not verify that URL. Check it is correct and try again.');
      throw err;
    }
  };

  return { validateUrl, isFetching, error };
};
